import { useExperiments } from '@alltrails/experiments';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';

/**
 * Hook for iOS-specific web checkout optimization experiment logic
 *
 * Experiment: web-growth-rm-mobile-to-web-checkout-optimization
 *
 * Detects iOS native app webview via:
 * - app_source=iOS query parameter
 *
 * Replaces the pattern:
 * `experiments['web-growth-rm-mobile-to-web-checkout-optimization']?.value !== 'control' && isIos`
 *
 * With explicit treatment variant checks and webview detection.
 */
export default function useIosWebCheckoutExperiment() {
  const experiments = useExperiments();
  const searchParams = useClientNavigationSearchParams();
  const isMobile = useIsMobileSizedScreen();

  const experimentValue = experiments['web-growth-rm-mobile-to-web-checkout-optimization']?.value;

  // Detect iOS native app webview
  const appSource = searchParams.get('app_source');
  const isIosNativeApp = isMobile && appSource === 'iOS';

  // Individual variant flags - only true for iOS native app users
  const isHierarchyUpdatesVariant = isIosNativeApp && experimentValue === 'hierarchy_updates';
  const isHierarchyAndCardCtaVariant = isIosNativeApp && experimentValue === 'hierarchy_and_card_cta';
  const isApplePayOnlyVariant = isIosNativeApp && experimentValue === 'apple_pay_only';
  const isHierarchyCardCtaAndTimelineVariant = isIosNativeApp && experimentValue === 'hierarchy_card_cta_and_timeline';
  const isAppStoreStyleVariant = isIosNativeApp && experimentValue === 'app_store_style';

  // Check if user is in a treatment variant (not control)
  const isTreatmentVariant =
    isHierarchyUpdatesVariant ||
    isHierarchyAndCardCtaVariant ||
    isApplePayOnlyVariant ||
    isHierarchyCardCtaAndTimelineVariant ||
    isAppStoreStyleVariant;

  // The iOS checkout optimization logic - only enabled for native app webviews (fixes payment buttons to bottom of page)
  const isIosWebCheckoutOptimizationEnabled = isIosNativeApp && isTreatmentVariant;

  // The logic included in all treatment variants except for A (hides the radio form and enables the button toggle)
  const isButtonToggleTreatmentVariant =
    isIosNativeApp && (isHierarchyAndCardCtaVariant || isApplePayOnlyVariant || isHierarchyCardCtaAndTimelineVariant || isAppStoreStyleVariant);

  return {
    isIosWebCheckoutOptimizationEnabled,
    isButtonToggleTreatmentVariant,
    isHierarchyUpdatesVariant,
    isHierarchyAndCardCtaVariant,
    isApplePayOnlyVariant,
    isHierarchyCardCtaAndTimelineVariant,
    isAppStoreStyleVariant,
    isIosNativeApp
  };
}
